import KeenSlider, { TOptionsEvents } from 'keen-slider';

// Types
type CarouselBreakpointOptions = Omit<TOptionsEvents, 'breakpoints'> & {
  disabled?: boolean;
};

// Custom carousel options, removing what we override since that's all managed
export type CarouselOptions = Omit<TOptionsEvents, (
  'breakpoints'
)> & {
  autoplay?: boolean;
  autoplaySpeed?: number;
  clickToSlide?: boolean;
  onSlideChange?: (carousel: Carousel, slide: number) => void;
  addVisible?: boolean;
  breakpoints?: { [key: string]: CarouselBreakpointOptions };
  variableWidth?: boolean;

  // Things from keen that we override and do not allow.
  move?: never;
  destroyed?: never;
  slideChanged?: never;
  dragStart?: never;
  dragEnd?: never;
  onSlideChanged?: never;
}

// Classes
const CLASS_PREV = 'is-prev-active';
const CLASS_NEXT = 'is-next-active';
const CLASS_ACTIVE = 'is-active';
const CLASS_INITIALIZED = 'is-initialized';
const CLASS_VISIBLE = 'is-visible';
const CLASS_KEEN_CONTAINER = 'keen-slider';
const CLASS_KEEN_VARIABLE = 'is-variable-width';
const CLASS_KEEN_SLIDE = 'keen-slider__slide';

const PROPERTY_PROGRESS = '--carousel-progress';
const PROPERTY_POSITION = '--carousel-position';

// Default Options
const CAROUSEL_DEFAULT_OPTIONS: CarouselOptions = {
  autoplay: false,
  autoplaySpeed: 10,
  controls: true
}

// Carousel Object Class
export class Carousel {
  private readonly container: HTMLElement;
  private readonly options: CarouselOptions;

  public readonly slides: HTMLElement[] = [];
  public slider: KeenSlider | null = null;

  private readonly buttonsNext: HTMLElement[] = [];
  private readonly buttonsPrev: HTMLElement[] = [];
  private readonly dots: CarouselDots[] = [];
  private readonly progress: HTMLElement[] = [];

  private readonly loadListening: HTMLElement[] = [];
  private readonly observer: IntersectionObserver;
  private refreshInterval: NodeJS.Timeout | null = null;

  private links: Carousel[] = [];

  /******************************* Constructor ********************************/
  constructor(
    container: HTMLElement, options: CarouselOptions = CAROUSEL_DEFAULT_OPTIONS
  ) {
    // Set up options.
    this.container = container;
    this.options = { ...CAROUSEL_DEFAULT_OPTIONS, ...options };

    // Find Slides
    container.childNodes.forEach(child => {
      if (typeof (child as HTMLElement).classList === typeof undefined) return;
      let element = (child as HTMLElement);
      this.slides.push(element);

      if (options.clickToSlide) {
        element.addEventListener('click', this.onSlideClick);
      }
    });

    // Event Listeners
    this.observer = new IntersectionObserver(this.onInteractionObserved);
    this.observer.observe(container);

    window.addEventListener('resize', this.onWindowResize);
    document.addEventListener('DOMContentLoaded', this.onDOMContentLoaded);
    document.addEventListener('load', this.onDOMLoaded);

    this.container.querySelectorAll('img').forEach(img => {
      if (img.complete) return;
      this.loadListening.push(img);
      img.addEventListener('load', this.onImageLoaded);
    });

    // Initialize.
    this.rebuild();
  }

  /***************************** Private Methods ******************************/
  private updateClasses() {
    if (!this.slider) {
      requestAnimationFrame(() => {
        if (this.slider) return;
        this.container.classList.remove(CLASS_INITIALIZED, CLASS_KEEN_CONTAINER, CLASS_KEEN_VARIABLE);
        this.slides.forEach(slide => {
          slide.classList.remove(CLASS_KEEN_SLIDE);
          slide.removeAttribute('style');
        });
      })
      return;// This may update before the slider has finished.
    }

    // Set up the slider
    this.container.classList.add(CLASS_KEEN_CONTAINER);
    this.slides.forEach(slide => slide.classList.add(CLASS_KEEN_SLIDE));
    if (this.options.variableWidth) this.container.classList.add(CLASS_KEEN_VARIABLE);

    // Calculate some slide deets
    const sliderDetails = this.slider.details();
    let slideIdx = sliderDetails.relativeSlide;
    this.links.forEach(link => {
      if (!link.slider) return;
      slideIdx = Math.max(slideIdx, link.slider.details().relativeSlide);
    });

    const numSlides = sliderDetails.size;
    const slide = this.slides[slideIdx];

    let nextIndex = slideIdx + 1;
    let prevIndex = slideIdx - 1;

    if (this.options.loop && nextIndex >= numSlides) nextIndex = 0;
    if (this.options.loop && prevIndex < 0) prevIndex = numSlides - 1;

    let slideNext = this.slides[nextIndex];
    let slidePrev = this.slides[prevIndex];

    // Manage slide classes
    this.slides.forEach(s => {
      s.classList.remove(CLASS_ACTIVE, CLASS_NEXT, CLASS_PREV);
    });

    slide?.classList.add(CLASS_ACTIVE);
    slideNext?.classList.add(CLASS_NEXT);
    slidePrev?.classList.add(CLASS_PREV);

    // Manage Button Classes
    this.buttonsNext.forEach(btnNext => {
      btnNext.classList.remove(CLASS_ACTIVE);
      if (slideNext) btnNext.classList.add(CLASS_ACTIVE);
    });

    this.buttonsPrev.forEach(btnPrev => {
      btnPrev.classList.remove(CLASS_ACTIVE);
      if (slidePrev) btnPrev.classList.add(CLASS_ACTIVE);
    });

    // Manage dots
    this.dots.forEach(dot => dot.setDot(slideIdx));

    // Update progress bar(s)
    const pbp = ((slideIdx + 1) / numSlides) * 100;
    this.progress.forEach(pb => {
      pb.style.setProperty(PROPERTY_PROGRESS, `${pbp}%`);
    })
  }

  private updateVisible() {
    if (!this.options.addVisible) return;

    const containerRect = this.container.getBoundingClientRect();
    this.slides.forEach(slide => {
      const slideRect = slide.getBoundingClientRect();
      const xIn = (
        slideRect.right > containerRect.x &&
        slideRect.left < containerRect.right
      );
      const yIn = (
        slideRect.bottom > containerRect.y &&
        slideRect.top < containerRect.bottom
      )

      if (xIn && yIn) {
        slide.classList.add(CLASS_VISIBLE);
      } else {
        slide.classList.remove(CLASS_VISIBLE);
      }
    });
  }

  private rebuild() {
    // Destroy the slider
    this.slider?.destroy();
    this.slider = null;

    // Do we need to rebuild?
    if (this.container.offsetWidth == 0 || this.container.offsetHeight == 0 || this.shouldBeDestroyed()) {
      this.updateClasses();
      return;
    }

    // Set up the slider
    this.container.classList.add(CLASS_KEEN_CONTAINER);
    this.slides.forEach(slide => slide.classList.add(CLASS_KEEN_SLIDE));

    const slider = new KeenSlider(this.container, {
      ...this.options,
      dragStart: this.onDragStart,
      dragEnd: this.onDragEnd,
      slideChanged: this.onSlideChanged,
      mounted: this.onMounted,
      destroyed: this.onDestroyed,
      move: this.onSlideMoved,
      afterChange: this.onAfterChange,
      beforeChange: this.onBeforeChange
    });
    this.slider = slider;

    this.updateClasses();
    this.refresh();
  }

  private shouldBeDestroyed() {
    return Object.entries(this.options.breakpoints || {}).some(bp => {
      const [key, value] = bp;
      if (!value.disabled) return false;
      return window.matchMedia(key).matches;
    });
  }

  /****************************** Public Methods ******************************/
  public refresh() {
    if (!this.slider) {
      this.rebuild();
      return;
    }

    if (this.shouldBeDestroyed()) {
      this.slider?.destroy();
      this.slider = null;
      return;
    }

    this.slider.resize();
    setTimeout(() => {
      this.slider?.moveToSlide(this.slider.details().absoluteSlide, 16);
      this.updateVisible();
    }, 16);
  }

  public addNextButton(container: HTMLElement) {
    this.buttonsNext.push(container);
    container.addEventListener('click', this.onNextClick);
    this.updateClasses();
  }

  public addPrevButton(container: HTMLElement) {
    this.buttonsPrev.push(container);
    container.addEventListener('click', this.onPrevClick);
    this.updateClasses();
  }

  public addProgress(container: HTMLElement) {
    this.progress.push(container);
    this.updateClasses();
  }

  public addDots(options: CarouselDotOptions) {
    const dots = new CarouselDots(this, options);
    this.dots.push(dots);
    return dots;
  }

  public removeNextButton(container: HTMLElement) {
    const ind = this.buttonsNext.indexOf(container);
    if (ind !== -1) this.buttonsNext.splice(ind, 1);
    container.removeEventListener('click', this.onNextClick);
  }

  public removePrevButton(container: HTMLElement) {
    const ind = this.buttonsPrev.indexOf(container);
    if (ind !== -1) this.buttonsPrev.splice(ind, 1);
    container.removeEventListener('click', this.onPrevClick);
  }

  public removeProgress(container: HTMLElement) {
    const ind = this.progress.indexOf(container);
    if (ind !== -1) this.progress.splice(ind, 1);
    container.style.setProperty(PROPERTY_PROGRESS, '0%');
  }

  public link(carousel: Carousel) {
    if (this.links.indexOf(carousel) === -1) this.links.push(carousel);
    if (carousel.links.indexOf(this) === -1) carousel.links.push(this);
  }

  public unlink(carousel: Carousel) {
    let i = this.links.indexOf(carousel);
    if (i !== -1) this.links.splice(i, 1);
    i = carousel.links.indexOf(this);
    if (i !== -1) carousel.links.splice(i, 1);
  }

  /********************************** Events **********************************/
  private onDragStart = () => {
  }

  private onDragEnd = () => {
  }

  private onSlideChanged = (slider: KeenSlider) => {
    // Update classes
    this.updateClasses();

    // Update linked slides
    const details = slider.details();
    this.links.forEach(link => {
      link.slider?.moveToSlide(details.absoluteSlide);
      link.updateClasses();
    });

    if (this.options.onSlideChange) {
      this.options.onSlideChange(this, details.relativeSlide);
    }
  }

  private onAfterChange = (slider: KeenSlider) => { }
  private onBeforeChange = (slider: KeenSlider) => { }

  private onMounted = (slider: KeenSlider) => {
    this.container.classList.add(CLASS_INITIALIZED);
    this.updateClasses();
  }

  private onDestroyed = () => {
    this.slider = null;
    this.updateClasses();
  }

  private onSlideMoved = (slider: KeenSlider) => {
    const details = slider.details();

    // Handle variable width
    if (this.options.variableWidth) {
      const boxes: DOMRect[] = [];

      // First, let's determine the sum of carousel slide widths.
      let totalWidth = 0;
      this.slides.forEach((slide, i) => {
        boxes[i] = slide.getBoundingClientRect();
        totalWidth += boxes[i].width;
        if (this.options.spacing) totalWidth += this.options.spacing;
      });

      // Now let's position each slide
      let currWidth = 0;
      const currentSlide = details.progressTrack * details.size;
      const sliderOffset = -totalWidth * details.progressTrack;
      this.slides.forEach((slide, i) => {
        let slideOffset = sliderOffset + currWidth;
        currWidth += boxes[i].width;
        if (this.options.spacing) currWidth += this.options.spacing;

        if (this.options.loop) {
          // If we loop, let's do this little hack to make loop work correctly.
          if (i < Math.floor(currentSlide)) slideOffset += totalWidth;
        } else if (details.position < 0) {
          // When not looping we need to make sure drags to the left are working
          slideOffset += totalWidth;
        }

        slide.style.transform = `translate3d(${slideOffset.toFixed(2)}px, 0, 0)`;
      });
    }

    // Handle visible classes
    this.updateVisible();

    this.container.style.setProperty(PROPERTY_POSITION, details.position.toString());
  }

  private onPrevClick = (e: MouseEvent) => { this.slider?.prev(); }
  private onNextClick = (e: MouseEvent) => { this.slider?.next(); }

  private onSlideClick = (e: MouseEvent) => {
    const ind = this.slides.indexOf(e.currentTarget as HTMLElement);
    this.slider?.moveToSlide(ind);
    this.links.forEach(l => l.slider?.moveToSlide(ind))
  }

  private onImageLoaded = () => this.refresh();

  private onWindowResize = () => this.refresh();

  private onInteractionObserved = () => {
    // Don't rebuild if not necessary.
    if (this.slider) return;
    this.rebuild();
  }

  private onDOMContentLoaded = () => this.refresh();

  private onDOMLoaded = () => this.refresh();

  /******************************** Destructor ********************************/
  public dispose() {
    this.slider?.destroy();

    [...this.buttonsNext].forEach(e => this.removeNextButton(e));
    [...this.buttonsPrev].forEach(e => this.removePrevButton(e));

    this.slides.forEach(slide => {
      slide.removeEventListener('click', this.onSlideClick);
    });

    window.removeEventListener('resize', this.onWindowResize);
    document.removeEventListener('DOMContentLoaded', this.onDOMContentLoaded);
    document.removeEventListener('load', this.onDOMLoaded);
    this.loadListening.forEach(load => {
      load.removeEventListener('load', this.onImageLoaded);
    });

    if (this.refreshInterval) clearInterval(this.refreshInterval);

    this.observer.disconnect();
    this.slider = null;
    this.updateClasses();
  }
}

// Dots
export type CarouselDotOptions = {
  container: HTMLElement;
  dotTemplate?: (dots: CarouselDots, slide: number) => HTMLElement;
  dotAdd?: (dots: CarouselDots, dot: HTMLElement, slide: number) => void;
  classContainer?: string;
  classActive?: string;
}

class CarouselDots {
  public readonly carousel: Carousel;
  public readonly options: CarouselDotOptions;

  private readonly dots: HTMLElement[] = [];

  constructor(carousel: Carousel, options: CarouselDotOptions) {
    this.carousel = carousel;
    this.options = options;

    carousel.slides.forEach((slide, i) => {
      // Create Dot
      const dot = this.dotCreate(i);

      // Append Dot
      if (this.options.dotAdd) {
        this.options.dotAdd(this, dot, i);
      } else {
        this.options.container.appendChild(dot);
      }
      this.dots.push(dot);

      // Dot event listeners
      dot.addEventListener('click', this.onDotClick);
    });

    // Set initial active state
    if (this.carousel.slider) {
      this.setDot(this.carousel.slider.details().relativeSlide);
    }
  }

  private dotCreate(slide: number) {
    if (this.options.dotTemplate) this.options.dotTemplate(this, slide);
    const container = document.createElement('button');
    container.setAttribute('title', (slide + 1).toString());
    container.innerHTML = `<span>${slide}</span>`;
    container.classList.add(this.options.classContainer || 'c-carousel-dot');
    return container;
  }

  public setDot(n: number): void {
    const classActive = this.options.classActive || CLASS_ACTIVE;
    n = Math.floor(n);
    this.dots.forEach((dot, i) => {
      if (i == n) {
        dot.classList.add(classActive);
      } else {
        dot.classList.remove(classActive)
      }
    });
  }

  private onDotClick = (e: MouseEvent) => {
    const i = this.dots.findIndex(d => d === e.currentTarget);
    if (i === -1) return;
    e.preventDefault();
    this.carousel.slider?.moveToSlide(i);
  }

  public dispose() {
    this.dots.forEach(dot => dot.removeEventListener('click', this.onDotClick));
  }
}
